import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Subscriptions as SubscriptionsIcon,
  CreditCard,
  Payment,
  Receipt,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Warning,
  Error,
  Info,
  Add,
  Edit,
  Delete,
  Visibility,
  Download,
  Refresh,
  Cancel,
  Upgrade,
  Star,
  StarBorder,
  Business,
  Person,
  Group,
  Timeline,
  AttachMoney,
  Schedule,
  Notifications,
  Settings,
  History,
  AccountBalance,
  LocalOffer,
  Loyalty,
  CardGiftcard,
} from '@mui/icons-material';
import subscriptionService, { Subscription, Plan } from '../services/subscriptionService';





const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [plansDialogOpen, setPlansDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [subscriptionsData, plansData] = await Promise.all([
          subscriptionService.getSubscriptions(),
          subscriptionService.getPlans()
        ]);
        setSubscriptions(subscriptionsData.subscriptions || []);
        setPlans(plansData);
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        // Set empty arrays as fallback
        setSubscriptions([]);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Ensure subscriptions is always an array
  const subscriptionsArray = Array.isArray(subscriptions) ? subscriptions : [];
  const plansArray = Array.isArray(plans) ? plans : [];

  const filteredSubscriptions = subscriptionsArray.filter(subscription => {
    const matchesSearch = subscription.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.planName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || subscription.status === statusFilter;
    const matchesPlan = planFilter === 'all' || subscription.planType === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const totalSubscriptions = subscriptionsArray.length;
  const activeSubscriptions = subscriptionsArray.filter(s => s.status === 'active').length;
  const trialSubscriptions = subscriptionsArray.filter(s => s.status === 'trial').length;
  const totalRevenue = subscriptionsArray.reduce((sum, s) => sum + (s?.totalRevenue || 0), 0);
  const monthlyRecurringRevenue = subscriptionsArray
    .filter(s => s.status === 'active')
    .reduce((sum, s) => {
      const monthlyAmount = s.billingCycle === 'yearly' ? (s?.amount || 0) / 12 : (s?.amount || 0);
      return sum + monthlyAmount;
    }, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'info';
      case 'cancelled': return 'error';
      case 'expired': return 'error';
      case 'suspended': return 'warning';
      default: return 'default';
    }
  };

  const getPlanColor = (planType: string) => {
    switch (planType) {
      case 'basic': return '#4caf50';
      case 'professional': return '#2196f3';
      case 'enterprise': return '#9c27b0';
      case 'custom': return '#ff9800';
      default: return '#757575';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (dateString === 'N/A') return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleViewDetails = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setDetailsDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Subscription Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<LocalOffer />}
            onClick={() => setPlansDialogOpen(true)}
          >
            View Plans
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ bgcolor: 'primary.main' }}
            onClick={() => navigate('/subscriptions/create')}
          >
            New Subscription
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SubscriptionsIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Subscriptions
                </Typography>
                <Typography variant="h5">
                  {totalSubscriptions}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircle color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Active
                </Typography>
                <Typography variant="h5">
                  {activeSubscriptions}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Info color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Trial Users
                </Typography>
                <Typography variant="h5">
                  {trialSubscriptions}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AttachMoney color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  MRR
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(monthlyRecurringRevenue)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search subscriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Plan</InputLabel>
            <Select
              value={planFilter}
              label="Plan"
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <MenuItem value="all">All Plans</MenuItem>
              <MenuItem value="basic">Basic</MenuItem>
              <MenuItem value="professional">Professional</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setPlanFilter('all');
            }}
          >
            Reset
          </Button>
        </Box>
      </Paper>

      {/* Subscriptions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Revenue</TableCell>
              <TableCell>Next Billing</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubscriptions.map((subscription) => (
              <TableRow key={subscription.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: getPlanColor(subscription.planType) }}>
                      {subscription.customerType === 'business' ? <Business /> : <Person />}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {subscription.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {subscription.customerEmail}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {subscription.planName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(subscription.amount)}/{subscription.billingCycle}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={subscription.status}
                    color={getStatusColor(subscription.status) as any}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {subscription.paymentMethod}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last: {formatDate(subscription.lastPayment)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {subscription.userCount}/{subscription.maxUsers} users
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(subscription.userCount / subscription.maxUsers) * 100}
                      sx={{ mt: 0.5, height: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {subscription.storageUsed}GB/{subscription.maxStorage}GB storage
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(subscription.totalRevenue)}
                  </Typography>
                  {subscription.discountApplied && (
                    <Chip
                      label={`-${subscription.discountApplied.percentage}%`}
                      size="small"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(subscription.nextBillingDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(subscription)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    {subscription.status === 'active' && (
                      <Tooltip title="Cancel">
                        <IconButton size="small" color="error">
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Subscription Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Subscription Details - {selectedSubscription?.customerName}
        </DialogTitle>
        <DialogContent>
          {selectedSubscription && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Card sx={{ flex: 1, minWidth: 250 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Plan Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Plan Name"
                          secondary={selectedSubscription.planName}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Status"
                          secondary={
                            <Chip
                              label={selectedSubscription.status}
                              color={getStatusColor(selectedSubscription.status) as any}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Billing Cycle"
                          secondary={selectedSubscription.billingCycle}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Amount"
                          secondary={formatCurrency(selectedSubscription.amount)}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
                
                <Card sx={{ flex: 1, minWidth: 250 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Usage & Limits
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Users: {selectedSubscription.userCount}/{selectedSubscription.maxUsers}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedSubscription.userCount / selectedSubscription.maxUsers) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Storage: {selectedSubscription.storageUsed}GB/{selectedSubscription.maxStorage}GB
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedSubscription.storageUsed / selectedSubscription.maxStorage) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                        color="secondary"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Features Included
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedSubscription.features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        variant="outlined"
                        size="small"
                        icon={<CheckCircle />}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
              
              {selectedSubscription.discountApplied && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Discount Applied: {selectedSubscription.discountApplied.code} 
                    (-{selectedSubscription.discountApplied.percentage}%, 
                    {formatCurrency(selectedSubscription.discountApplied.amount)} off)
                  </Typography>
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" startIcon={<Receipt />}>
                  View Invoices
                </Button>
                <Button variant="outlined" startIcon={<History />}>
                  Payment History
                </Button>
                <Button variant="outlined" startIcon={<Settings />}>
                  Manage Plan
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Plans Dialog */}
      <Dialog
        open={plansDialogOpen}
        onClose={() => setPlansDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Available Plans
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
            {plans.map((plan) => (
              <Card
                key={plan.id}
                sx={{
                  flex: 1,
                  minWidth: 280,
                  position: 'relative',
                  border: plan.popular ? '2px solid' : '1px solid',
                  borderColor: plan.popular ? 'primary.main' : 'divider',
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                    }}
                  >
                    Most Popular
                  </Box>
                )}
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="h5" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {plan.description}
                  </Typography>
                  
                  <Box sx={{ my: 3 }}>
                    <Typography variant="h3" component="div" color="primary">
                      {formatCurrency(plan.price.monthly)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      per month
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(plan.price.yearly)} billed annually
                    </Typography>
                  </Box>
                  
                  <List dense>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  
                  <Button
                    variant={plan.popular ? 'contained' : 'outlined'}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Select Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlansDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscriptions;